import React, { useEffect, useState, useMemo } from "react"
import { Row, Col, Tabs, Space, Divider, Spin, Typography, Card } from "antd"
import StatCard from "../components/StatCard"
import QuickActions from "../components/QuickActions"
import NotificationPanel from "../components/NotificationPanel"
import MetabaseSection from "../components/MetabaseSection"
import AdminControlPanel from "../components/AdminControlPanel"
import {
  UserOutlined,
  ProjectOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  ToolOutlined,
  BankOutlined,
} from "@ant-design/icons"
import { getAdminDashboardApi } from "helpers/backend_helper"
import BankTransactions from "../components/BankTransactions"
import ListExecutor from "../components/ListExecutor"
const { Title } = Typography

const AdminDashboard = ({
  infoMessage,
  telegramLink,
  iframeUrl,
  inactiveDes,
  setInactiveDes,
  clientSetting,
  onInactiveClient,
  clientLoading,
  userInfo,
  showMetabase = true,
}) => {
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedUserId, setSelectedUserId] = useState(null)

  // Fetch dashboard data from backend
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        const response = await getAdminDashboardApi()
        setDashboardData(response)
      } catch (error) {
        console.error("Failed to fetch admin dashboard data:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [])

  // Build stats from API response - categorized
  const userStats = dashboardData
    ? [
        {
          title: "کارشناسان فعال",
          value: dashboardData.totalActiveEngineers || 0,
          icon: <UserOutlined />,
          suffix: "نفر",
        },
        {
          title: "مجری‌های فعال",
          value: dashboardData.totalActiveExecutors || 0,
          icon: <TeamOutlined />,
          suffix: "نفر",
        },
        {
          title: "نصاب‌های فعال",
          value: dashboardData.totalActivePackageInstallers || 0,
          icon: <ToolOutlined />,
          suffix: "نفر",
        },
      ]
    : []

  const projectStats = dashboardData
    ? [
        {
          title: "کل پرونده‌ها",
          value: dashboardData.totalActiveProjects || 0,
          icon: <ProjectOutlined />,
          suffix: "پرونده",
        },
        {
          title: "در انتظار تخصیص",
          value: dashboardData.projectsInNullStage || 0,
          icon: <ClockCircleOutlined />,
          suffix: "پرونده",
        },
        {
          title: "در انتظار تایید",
          value: dashboardData.projectsInMapStagePending || 0,
          icon: <ClockCircleOutlined />,
          suffix: "پرونده",
        },
        {
          title: "تایید شده",
          value: dashboardData.projectsInMapStageApproved || 0,
          icon: <CheckCircleOutlined />,
          suffix: "پرونده",
          trend: "up",
        },
      ]
    : []

  // Tab items for Ant Design 5
  const tabItems = useMemo(() => {
    const items = [
      {
        key: "1",
        label: "نمای کلی",
        children: (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={8}>
              <QuickActions role="Administrator" />
            </Col>
            <Col xs={24} lg={16}>
              <NotificationPanel
                telegramLink={null}
                showTelegram={false}
                maxItems={5}
              />
            </Col>
          </Row>
        ),
      },
    ]

    if (showMetabase) {
      items.push({
        key: "2",
        label: "داشبورد تحلیلی",
        children: <MetabaseSection iframeUrl={iframeUrl} loading={!iframeUrl} />,
      })
    }

    items.push(
      {
        key: "3",
        label: "تنظیمات سیستم",
        children: (
          <AdminControlPanel
            inactiveDes={inactiveDes}
            setInactiveDes={setInactiveDes}
            clientSetting={clientSetting}
            onInactiveClient={onInactiveClient}
            clientLoading={clientLoading}
          />
        ),
      },
         {
      key: "4",
      label: (
        <Space>
          <BankOutlined />
          تراکنش‌های بانکی
        </Space>
      ),
      children: (
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <div style={{ marginBottom: 16 }}>
              <Space align="center" style={{ marginBottom: 8 }}>
                <TeamOutlined style={{ color: "#1890ff" }} />
                <span style={{ fontWeight: 600 }}>انتخاب مجری</span>
              </Space>
              <ListExecutor
                selectedUserId={selectedUserId}
                onSelectUser={setSelectedUserId}
              />
            </div>
            <Divider style={{ margin: "12px 0" }} />
            <BankTransactions userId={selectedUserId} canReconcile />
          </Col>
        </Row>
      ),
    },
      {
        key: "5",
        label: "گزارش‌های سیستم",
        children: (
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <StatCard
                title="این بخش در آینده پیاده‌سازی خواهد شد"
                value="به زودی"
                icon={<ProjectOutlined />}
              />
            </Col>
          </Row>
        ),
      },
      {
        key: "6",
        label: "مدیریت کاربران",
        children: (
          <Row gutter={[16, 16]}>
            <Col xs={24}>
              <StatCard
                title="مدیریت کاربران"
                value="به زودی"
                icon={<UserOutlined />}
              />
            </Col>
          </Row>
        ),
      }
    )

    return items
  }, [
    infoMessage,
    showMetabase,
    iframeUrl,
    inactiveDes,
    setInactiveDes,
    clientSetting,
    onInactiveClient,
    clientLoading,
    selectedUserId,
  ])

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Statistics Cards */}
      {loading ? (
        <Spin size="large" tip="در حال بارگذاری داده‌ها...">
          <div style={{ textAlign: "center", padding: "40px", minHeight: 200 }} />
        </Spin>
      ) : (
        <>
          {/* Users Section */}
          <div>
            <Title level={5} style={{ color: "#1890ff" }}>
              👥 کاربران سیستم
            </Title>
            <Row gutter={[16, 16]}>
              {userStats.map((stat, index) => (
                <Col xs={24} sm={12} lg={8} key={index}>
                  <StatCard {...stat} />
                </Col>
              ))}
            </Row>
          </div>


          {/* Projects Section */}
          <div>
            <Title level={5} style={{  color: "#52c41a" }}>
              📁 وضعیت پرونده‌ها
            </Title>
            <Row gutter={[16, 16]}>
              {projectStats.map((stat, index) => (
                <Col xs={24} sm={12} lg={6} key={index}>
                  <StatCard {...stat} />
                </Col>
              ))}
            </Row>
          </div>
        </>
      )}

      {/* Main Content Area */}
      <Tabs 
        defaultActiveKey="1" 
        size="large" 
        type="card"
        items={tabItems}
      />
    </Space>
  )
}

export default AdminDashboard
